import { ChevronDown, HelpCircle, ScrollText } from "lucide-react";
import { ConfidenceChip } from "../progress/ConfidenceChip";
import { formatShortDate } from "../progress/utils";
import { latestCalorieTargetEntry, latestEntriesPerKind, type CalculationLogEntry } from "../../screens/plan-utils";

export interface AuditDisclosureProps {
  readonly history: readonly CalculationLogEntry[];
  readonly engineVersion: string;
}

function kindLabel(kind: string): string {
  switch (kind) {
    case "calorieTarget":
      return "Calorie target";
    case "macroTargets":
      return "Macro targets";
    case "initialTdee":
      return "Initial expenditure";
    case "adaptiveExpenditure":
      return "Adaptive expenditure";
    case "weeklyAdjustment":
      return "Weekly adjustment";
    case "trendWeight":
      return "Trend weight";
    case "goalDuration":
      return "Goal timeline";
    case "forecast":
      return "Forecast";
    case "bmi":
      return "BMI";
    case "bmr":
      return "Metabolic rate";
    default:
      return kind;
  }
}

/** "Why this target?" — latest calorie-target explanation in plain language. */
export function WhyTargetDisclosure({ history }: { readonly history: readonly CalculationLogEntry[] }) {
  const entry = latestCalorieTargetEntry(history);
  if (entry === null) return null;
  return (
    <details className="rounded-2xl border border-border bg-surface p-4 shadow-sm" data-testid="why-target-disclosure">
      <summary className="flex min-h-[44px] cursor-pointer list-none items-center gap-2 text-base font-semibold text-text active:opacity-80">
        <HelpCircle size={18} className="text-accent" />
        Why this target?
        <ChevronDown size={16} className="disclosure-chevron ml-auto text-text-muted" aria-hidden />
      </summary>
      <div className="mt-2 flex flex-col gap-2">
        <p className="text-sm leading-relaxed text-text-muted" data-testid="why-target-explanation">
          {entry.explanation}
        </p>
        <p className="text-xs text-text-muted">Calculated {formatShortDate(entry.createdAt)}</p>
      </div>
    </details>
  );
}

/** Advanced "Calculation audit" — latest engine entry per kind with versions, never raw JSON. */
export function AuditDisclosure({ history, engineVersion }: AuditDisclosureProps) {
  const latest = latestEntriesPerKind(history);
  return (
    <details className="rounded-2xl border border-border bg-surface p-4 shadow-sm" data-testid="audit-disclosure">
      <summary className="flex min-h-[44px] cursor-pointer list-none items-center gap-2 text-base font-semibold text-text active:opacity-80">
        <ScrollText size={18} className="text-accent" />
        Calculation audit
        <ChevronDown size={16} className="disclosure-chevron ml-auto text-text-muted" aria-hidden />
      </summary>
      <div className="mt-2 flex flex-col gap-3">
        <p className="text-xs text-text-muted" data-testid="audit-engine-version">
          Engine version {engineVersion}
        </p>
        {history.length === 0 ? (
          <p className="text-sm text-text-muted" data-testid="audit-empty">
            No calculations recorded yet — they appear once your plan is computed.
          </p>
        ) : (
          <ul className="flex flex-col gap-2" data-testid="audit-list">
            {latest.map((entry, index) => (
              <li
                key={`${entry.kind}-${entry.createdAt}-${index}`}
                className="rounded-xl bg-muted/60 px-3 py-2"
                data-testid="audit-entry"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-semibold text-text">{kindLabel(entry.kind)}</span>
                  <ConfidenceChip level={entry.confidence} />
                </div>
                {entry.explanation.trim().length > 0 && (
                  <p className="mt-1 text-[11px] leading-relaxed text-text-muted">{entry.explanation}</p>
                )}
                <p className="mt-1 text-[10px] text-text-muted">
                  Engine v{entry.engineVersion} · Algorithm v{entry.algorithmVersion} · {formatShortDate(entry.createdAt)}
                </p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </details>
  );
}
